import { useAtom } from '@reatom/npm-react'
import BigNumber from 'bignumber.js'

import { CurrencyModel } from '@root/entities/currency'
import { useOptionalAtom } from '@root/shared/lib'

import { walletsModelByNetwork, type WalletModel } from '../atoms'

type BalanceArgs = {
  wallet: WalletModel | undefined
  currency: CurrencyModel | undefined
  treatWrappedAsNative?: boolean
}

const useWalletAsset = ({
  currency: currency_,
  wallet,
  treatWrappedAsNative,
}: BalanceArgs) => {
  const currency = (() => {
    if (!currency_) {
      return undefined
    }
    if (treatWrappedAsNative) {
      return CurrencyModel.toNativeIfWrapped(currency_)
    }
    return currency_
  })()

  const [asset] = useAtom(
    (ctx) => {
      if (!currency || !wallet) {
        return undefined
      }
      const assets = ctx.spy(wallet.assetsByCurrency)
      const asset = assets[currency.address]
      return asset
    },
    [currency, wallet],
  )
  return asset
}

export const useWalletAssetBalance = ({
  currency: currency_,
  wallet,
  treatWrappedAsNative = true,
}: {
  wallet: WalletModel | undefined
  currency: CurrencyModel | undefined
  treatWrappedAsNative?: boolean
}) => {
  const asset = useWalletAsset({
    currency: currency_,
    wallet,
    treatWrappedAsNative,
  })

  const [balance] = useAtom(
    (ctx) => {
      return asset ? ctx.spy(asset.balance) : BigNumber(0)
    },
    [asset],
  )
  return balance
}

export const useWalletAssetBalanceUsd = ({
  currency: currency_,
  wallet,
  treatWrappedAsNative = true,
}: {
  wallet: WalletModel | undefined
  currency: CurrencyModel | undefined
  treatWrappedAsNative?: boolean
}) => {
  const asset = useWalletAsset({
    currency: currency_,
    wallet,
    treatWrappedAsNative,
  })

  const balance = useOptionalAtom(asset?.balanceUsd, BigNumber(0))
  return balance
}

export const useWalletsAssetBalance = ({
  currency,
}: {
  currency: CurrencyModel
}) => {
  const walletsModel = walletsModelByNetwork[currency.network]
  const walletAssetBalanceAtom = walletsModel.assetBalance(currency.address)
  const [balance] = useAtom(walletAssetBalanceAtom)
  return balance
}

export const useWalletsAssetBalanceUsd = ({
  currency,
}: {
  currency: CurrencyModel
}) => {
  const walletsModel = walletsModelByNetwork[currency.network]
  const walletAssetBalanceAtom = walletsModel.assetBalanceUsd(currency.address)
  const [balance] = useAtom(walletAssetBalanceAtom)
  return balance
}
